/**
 * Reason-held player control lock. Every system that takes the controls away (a cutscene, a menu, an
 * arrest, an interior) acquires its own reason and releases that reason when it is done; the player has
 * the controls back once no reason is held. One shared on/off switch written by fifteen systems used to
 * let one of them hand the wheel back while another still wanted it (a result sheet over a mission
 * outro, the book over a cutscene).
 *
 * A reason is held at most once: acquiring a held reason again is a no-op (per-frame callers are fine).
 * Releasing a reason that is not held is a no-op with a dev warning; `drop` is the quiet version for
 * cleanup paths that cannot know. The safety nets (respawn, load, mission end) `drop` stale reasons.
 * `__bh.controlLock()` reads the held reasons.
 */

declare module '../core/hooks' {
  interface BhHooks {
    /** The reasons holding the player's controls right now ([] = the player can move). */
    controlLock(): string[];
  }
}

export type ControlReason =
  /** The title screen / sign-in (before the game starts). */
  | 'title'
  /** A job's intro or outro beat (missions/runner.ts). */
  | 'mission'
  /** A race's three-two-one on the grid (activities/race.ts). */
  | 'raceCountdown'
  /** Nicked / wasted offline, until the respawn (economy). */
  | 'outcome'
  /** Nicked / wasted run by the police module's own respawn timer. */
  | 'policeOutcome'
  /** A result sheet is up (ui). */
  | 'resultSheet'
  /** In the respray shop with the shutter down (economy). */
  | 'respray'
  | 'book'
  | 'garageCard'
  | 'sprayBay'
  | 'trade'
  | 'hideout'
  | 'backRoom'
  /** The profile page is up (src/profile). */
  | 'profile'
  /** The first-run notes (onboarding). */
  | 'onboarding'
  /** Online: the connection is down (online-ui). */
  | 'connection'
  /** Online: a clipping / panel is open (online-ui). */
  | 'onlinePanel'
  /** Online: lying where you fell until the server's respawn time (online-ui outcomes). */
  | 'onlineDown';

/** Reasons a job owns: dropped when a job ends however it ends. */
export const MISSION_REASONS: readonly ControlReason[] = ['mission', 'raceCountdown'];
/**
 * Reasons that cannot outlive a respawn: the arrest / death and whatever job or shop visit was going on.
 * Screens with their own close (book, result sheet, interiors) keep theirs: they hand it back on close.
 */
export const RESPAWN_REASONS: readonly ControlReason[] = ['mission', 'raceCountdown', 'outcome', 'policeOutcome', 'respray', 'onlineDown'];

export class ControlLock {
  private held = new Set<ControlReason>();

  constructor(private readonly warn: (msg: string) => void = defaultWarn) {}

  /** True while no reason holds the controls. */
  get enabled(): boolean {
    return this.held.size === 0;
  }

  /** The reasons holding the controls, in the order they were taken. */
  get reasons(): ControlReason[] {
    return [...this.held];
  }

  holds(reason: ControlReason): boolean {
    return this.held.has(reason);
  }

  acquire(reason: ControlReason): void {
    this.held.add(reason);
  }

  release(reason: ControlReason): void {
    if (!this.held.delete(reason)) this.warn(`[controlLock] release('${reason}') but it is not held (held: ${this.reasons.join(', ') || 'none'})`);
  }

  /** `acquire` when `on`, otherwise `drop` (quiet): for callers that sync a state every frame. */
  set(reason: ControlReason, on: boolean): void {
    if (on) this.held.add(reason);
    else this.held.delete(reason);
  }

  /** Release without the warning: cleanup paths that may or may not hold `reason`. */
  drop(...reasons: readonly ControlReason[]): void {
    for (const r of reasons) this.held.delete(r);
  }

  /** Safety net: drop every reason (a load / new game). */
  clear(): void {
    this.held.clear();
  }
}

function defaultWarn(msg: string): void {
  if (import.meta.env?.DEV) console.warn(msg);
}
